import { useState, useRef } from 'react';
import { motion } from 'framer-motion';

interface Project {
  id: string;
  title: string;
  year: string;
  description: string;
  tags: string[];
  videoUrl?: string;
  imageUrl?: string;
  link: string;
  size: 'large' | 'medium' | 'square';
  comingSoon?: boolean;
  imageStyle?: string;
}

interface ProjectCardProps {
  project: Project;
  nested?: boolean;
}

export default function ProjectCard({ project, nested }: ProjectCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleMouseEnter = () => {
    setIsHovered(true);
    if (videoRef.current && !project.comingSoon) {
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {});
      }
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const getImageHeight = () => {
    if (project.size === 'square') {
      return 'h-[200px]';
    }
    if (nested) {
      return project.size === 'large' ? 'h-[300px]' : 'h-[240px]';
    }
    return project.size === 'large' ? 'h-[300px]' : 'h-[280px]';
  };

  const imageHeight = getImageHeight();

  return (
    <motion.a
      href={project.comingSoon ? undefined : project.link}
      data-project-card
      data-coming-soon={project.comingSoon ? 'true' : undefined}
      className={`block group ${project.comingSoon ? 'cursor-default' : 'cursor-pointer'}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={(e) => {
        if (project.comingSoon) {
          e.preventDefault();
        }
      }}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div
        className={`relative overflow-hidden rounded-lg border border-notion-border transition-shadow duration-300 h-full ${
          nested ? 'bg-white' : 'bg-notion-sidebar'
        } ${isHovered && !project.comingSoon ? 'shadow-md' : 'shadow-none'}`}
      >
        {/* Media Container */}
        <div className={`relative w-full ${imageHeight} overflow-hidden bg-notion-hover`}>
          {project.videoUrl && (
            <video
              ref={videoRef}
              src={project.videoUrl}
              loop
              muted
              playsInline
              className={`absolute inset-0 w-full h-full object-cover transition-transform duration-500 ${
                isHovered && !project.comingSoon ? 'scale-[1.02]' : 'scale-100'
              }`}
            />
          )}
          {project.imageUrl && !project.videoUrl && (
            <img
              src={project.imageUrl}
              alt={project.title}
              title={`${project.title} - Annie Ding Portfolio`}
              className={`absolute inset-0 w-full h-full object-top transition-transform duration-500 ${
                project.imageStyle ? project.imageStyle : 'object-cover'
              } ${
                isHovered && !project.comingSoon ? 'scale-[1.02]' : 'scale-100'
              }`}
            />
          )}
        </div>

        {/* Content */}
        <div className="p-5">
          <div className="flex items-start justify-between mb-2">
            <h3 className={`text-[15px] font-medium text-notion-text transition-colors ${!project.comingSoon && 'group-hover:text-notion-accent'}`}>
              {project.title}
            </h3>
            <span className="text-xs text-notion-secondary ml-4 whitespace-nowrap bg-notion-tag px-2 py-0.5 rounded">
              {project.year}
            </span>
          </div>
          <p className="text-[13px] text-notion-secondary leading-relaxed mb-3">
            {project.description}
          </p>
          {/* Tags */}
          <div className="flex flex-wrap gap-1.5">
            {project.tags.map((tag) => (
              <span
                key={tag}
                className="text-[11px] text-notion-secondary bg-notion-tag px-2 py-0.5 rounded"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </motion.a>
  );
}
